import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { ShieldCheck, Mail, ArrowRight } from "lucide-react";

const EFFECTIVE_DATE = "June 1, 2026";

export default function PrivacyPage() {
  useEffect(() => {
    const prev = document.title;
    document.title = "Privacy Policy · AIGL Channel";
    return () => { document.title = prev; };
  }, []);

  return (
    <div data-testid="privacy-page" className="min-h-screen bg-white">
      <Header />

      <section className="bg-[#0A192F] text-white">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12 pt-16 sm:pt-24 pb-14 sm:pb-20">
          <div className="font-accent text-[11px] tracking-[0.3em] text-[#C5A059] mb-5 flex items-center gap-3">
            <ShieldCheck className="w-4 h-4" /> PRIVACY POLICY
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            Your data. <span className="text-[#D4AF37] italic">Your business.</span>
          </h1>
          <p className="mt-6 text-white/70 max-w-2xl">
            We collect only what we need to deliver your purchased plans and keep your account secure. We never sell your data.
          </p>
          <div className="mt-7 inline-block font-accent text-[10px] tracking-[0.25em] text-white/50 border border-white/20 px-3 py-1.5">
            EFFECTIVE {EFFECTIVE_DATE.toUpperCase()}
          </div>
        </div>
      </section>

      <article className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12 py-16 sm:py-20 prose-style">
        <Section number="01" title="What we collect">
          <p>
            When you purchase a plan or sign up for updates, we collect:
          </p>
          <ul>
            <li><b>Name</b> — to personalize order confirmations and download emails.</li>
            <li><b>Email address</b> — to deliver your secure download link and any transactional notifications.</li>
            <li><b>Purchase information</b> — the plan(s) you bought, amount, currency, and timestamp. Card details are processed by Stripe and PayPal directly; AIGL <b>never</b> sees or stores your full card number.</li>
            <li><b>Anonymous analytics</b> — page views and conversion events via Google Analytics / Google Ads to improve the storefront. No personally identifiable data is sent to advertisers.</li>
          </ul>
        </Section>

        <Section number="02" title="How we use it">
          <ul>
            <li>Fulfilling your order: generating a secure download link and emailing your PDF plan(s).</li>
            <li>Sending transactional emails (order confirmations, download reminders, review prompts) through our email delivery partner, <b>MailerSend</b>.</li>
            <li>Supporting you when you write to us with a question or refund request.</li>
            <li>Internal product analytics — understanding which plans sell, where buyers come from, and how to improve the buying experience.</li>
          </ul>
        </Section>

        <Section number="03" title="What we never do">
          <ul>
            <li><b>We do not sell your data to third parties.</b> Ever.</li>
            <li>We do not rent your email list, share it with partners, or use it for ads outside of AIGL Channel.</li>
            <li>We do not store your card details. Payments are processed by Stripe and PayPal under their own PCI-DSS-compliant infrastructure.</li>
          </ul>
        </Section>

        <Section number="04" title="Subprocessors we trust">
          <ul>
            <li><b>Stripe</b> &amp; <b>PayPal</b> — to process card / wallet payments.</li>
            <li><b>MailerSend</b> — to deliver your purchase emails and reminders.</li>
            <li><b>Google Analytics / Google Ads</b> — anonymous traffic + conversion analytics.</li>
            <li><b>MongoDB Atlas</b> — encrypted database hosting our order records.</li>
          </ul>
        </Section>

        <Section number="05" title="Your rights">
          <p>You can request at any time:</p>
          <ul>
            <li>A copy of every record we hold about you.</li>
            <li>Correction of any inaccurate data.</li>
            <li>Deletion of your account and order history (subject to legal retention obligations for tax/payment records).</li>
            <li>Unsubscribe from non-transactional emails via the link in every message.</li>
          </ul>
          <p>Send any of these requests to <a href="mailto:aiglchannel.info@gmail.com" className="text-[#C5A059] hover:text-[#D4AF37]">aiglchannel.info@gmail.com</a> and we'll action it within 14 days.</p>
        </Section>

        <Section number="06" title="Cookies">
          <p>
            We use minimal first-party cookies for session integrity (admin login) and an anonymous Google Analytics cookie. No advertising or cross-site tracking cookies.
          </p>
        </Section>

        <Section number="07" title="Children">
          <p>AIGL Channel is intended for adults building businesses. We do not knowingly collect data from anyone under 16. If you believe a minor has submitted personal data, please contact us and we will delete it.</p>
        </Section>

        <Section number="08" title="Changes to this policy">
          <p>
            We may update this policy as our service evolves. Material changes will be announced via an email to anyone with an active account. The current "Effective" date at the top of this page reflects the latest revision.
          </p>
        </Section>

        <div className="mt-14 p-7 bg-[#FBF8F1] border-l-4 border-[#C5A059]">
          <div className="flex items-start gap-4">
            <Mail className="w-6 h-6 text-[#C5A059] flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-display text-xl text-[#0A192F]">Questions or requests?</div>
              <p className="mt-1 text-slate-600">
                Write to{" "}
                <a href="mailto:aiglchannel.info@gmail.com" data-testid="privacy-contact-email" className="font-bold text-[#0A192F] hover:text-[#C5A059]">
                  aiglchannel.info@gmail.com
                </a>
                . We answer every email within one business day.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-10 flex gap-3">
          <Link to="/support" data-testid="privacy-to-support" className="inline-flex items-center gap-2 bg-[#0A192F] hover:bg-[#0A192F]/90 text-white font-accent text-xs tracking-[0.2em] px-5 py-3 transition">
            VISIT SUPPORT
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
          <Link to="/" data-testid="privacy-to-home" className="inline-flex items-center gap-2 border border-slate-300 hover:border-[#0A192F] text-slate-700 hover:text-[#0A192F] font-accent text-xs tracking-[0.2em] px-5 py-3 transition">
            BACK TO STOREFRONT
          </Link>
        </div>
      </article>

      <Footer />
    </div>
  );
}

const Section = ({ number, title, children }) => (
  <section className="mt-10 first:mt-0">
    <div className="font-accent text-[10px] tracking-[0.3em] text-[#C5A059]">{number}</div>
    <h2 className="mt-2 font-display text-2xl sm:text-3xl text-[#0A192F] mb-4 leading-tight">{title}</h2>
    <div className="text-slate-700 leading-relaxed space-y-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-2 [&_a]:underline [&_a]:underline-offset-2">
      {children}
    </div>
  </section>
);
